import { CrudActions, FORM_TRIGGER_TYPES, SidebarContainer } from "utils/constants";
import { ApiPageTab } from "./apiTypes";
import {
  Action,
  SearchTableAdditionalConfig,
  BAFile,
  Page,
  TableColumnType,
  TableFilterType,
  TableViewConfig
} from "./baTypes";
export interface Dictionary<T> {
  [index: string]: T;
}

export type Middleware<S, A> = (state: S, action: A, next: (action: A) => void) => void;

export type RadioGroupDirectionProps = "horizontal" | "vertical" | undefined;

export type UpdatedStaticDealsPage = {
  viewConfig?: TableViewConfig;
};

export type NestedViewRecordProps =
  | {
      isNestedView?: boolean;
      queryParams?: undefined;
    }
  | {
      isNestedView?: boolean;
      queryParams: {
        tabId: string;
        tabPath: string;
        nestedRecordId: string;
      }[];
    }
  | undefined;

export type RecordItem = Record<string, any>;
export type SelectOption = {
  title: string;
  value: any;
  record?: RecordItem;
  optionData?: RecordItem;
  column?: TableColumnType;
} & RecordItem;

export type SelectOptions = SelectOption[];
export type SelectValue = SelectOption | SelectOptions | null;

export type AddressValue = {
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
};

export type AddressComplete = {
  id: string;
  longitude?: number;
  latitude?: number;
} & AddressValue;

export type PlacesAutoCompleteAddressResult = {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
};

export type RouteError = { error: Error; reset: () => void };
export type FileUploadInput = {
  file: File;
  path: string;
  name: string;
} & RecordItem;

export type FileUploadResults = {
  uploadCount: number;
  uploadedFiles: FileUploadInput[];
  errorFileData: FileUploadInput[];
};

export interface GridViewColumn extends TableColumnType {
  renderHeader?: (...args: any[]) => React.ReactElement;
  disableSorting?: boolean; // this can be moved to TableColumnType if we plan to store in DB later
  cell?: (...args: any[]) => React.ReactElement;
}

export type TableColumnChangeProps = {
  id: string;
  newProps: Partial<TableColumnType> | Partial<TableFilterType>;
  callSave?: boolean;
  addLookupLabelsCheck?: boolean;
};

export type NavigationItemAction = {
  id: string;
  name: string;
  type: string;
  properties?: any;
  icon?: string;
  waitForSuccess?: boolean;
  tabPageId?: string; // Added in DetailTabView to be used in webhook requests
  tableName?: string; // Added in DetailTabView to be used in webhook requests
  onClick?: () => void; // Added in menu actions to rename action
  hasDivider?: boolean;
  ui_action_id?: Action;
  accessRoleIds?: string[];
  sort?: string;
};

export type MenuItemAdditionalConfig = {
  showAsCreateForm?: boolean;
  createFormViewId?: string;
  displayCreateFormAsModal?: boolean;
  defaultOpen?: boolean; // used to store default state for folder/section
  useCustomView?: boolean; // used to trigger custom view for menu item (currently custom views only support form)
  sortOrderByRoleId?: { [roleId: string]: string }; // Sort order override by role
};

export type NavigationItem = {
  id: string;
  name: string;
  href: string | { pathname: string; query: { [key: string]: string | string[] } };
  children?: NavigationItem[];
  actions?: NavigationItemAction[];
  is_divider?: boolean;
  icon?: string;
  ui_page_id?: Page;
  is_section?: boolean;
  is_folder?: boolean;
  onClick?: () => void;
  isActive?: boolean;
  open_in_new_tab?: boolean;
  onMouseDown?: () => void;
  tabPageId?: string;
  isAdminMenuItem?: boolean;
  showAdminOnly?: boolean;
  show_record_count?: boolean;
  additionalConfig?: MenuItemAdditionalConfig;
  tableName?: string;
  ui_menu_id?: string;
  isViewOnlyMenu?: boolean; // Used when defined the menu in client side
  sort_order?: string;
  parent_id?: string;
  showExposedActions?: boolean;
  accessRoleIds?: string[];
  iconClassName?: string;
  activeIconClassName?: string;
  tab?: ApiPageTab;
  callbackOnClick?: boolean; // Calls onClick on item even if href
  skipActiveCheck?: boolean; // We use pathIsActive within the Nav component to check this, this is skipped if we send this prop as true
  viewId?: string; // Used to identify the custom viewId of the menu item
};

export type QueryHookOptions = {
  refetchOnWindowFocus?: boolean;
  refetchOnMount?: boolean;
  enabled?: boolean;
  refetchInterval?: false | number;
  staleTime?: number;
  gcTime?: number;
  refetchOnReconnect?: boolean;
  networkMode?: "online" | "always" | "offlineFirst";
  retryOnMount?: boolean;
};

export type CardData = {
  row: RecordItem;
  cells: React.ReactElement[];
  featuredImage?: React.ReactElement | null;
  selected?: boolean;
  header?: RecordItem;
  formattedRecordVal?: RecordItem;
  conditionalStyles?: RecordItem;
  isPinned?: boolean;
};

export enum ActionHandlerDisplayTypes {
  CELL = "cell"
}

export type BulkActionState = {
  selectedRows?: RecordItem[];
  isSelected?: boolean;
  isModalOpen?: boolean;
  bulkAction?: "edit" | "delete";
  tablePageSlug?: string;
  tablePageId?: string;
  tableName?: string;
  uploadedFilesSelection?: RecordItem[];
  getAllRecords?: boolean; // When set to true the view will fetch all records and select them for bulk edit
  allRecordsFetchLoading?: boolean;
  isAllRecordsEdit?: boolean; // this is used to reset pagination and update all records
};

export type PlayerMeta = {
  title: string;
  audio: {
    src?: string;
    type: string;
  };
};

export type PlayerState = {
  playing: boolean;
  muted: boolean;
  duration: number;
  currentTime: number;
  meta: PlayerMeta | null;
};

export type PlayerActions = {
  play: (data?: PlayerMeta) => void;
  pause: () => void;
  toggle: (data?: PlayerMeta) => void;
  toggleMute: () => void;
  isPlaying: (data?: PlayerMeta) => boolean;
  seek: (time: number) => void;
  seekBy: (time: number) => void;
  playbackRate: (rate: number) => void;
  close: () => void;
  getPlayerReady: (data?: PlayerMeta) => void;
};

export type Player = PlayerState & PlayerActions;
export type SearchTableColumnChangeProps = {
  id: string;
  newProps: Partial<TableColumnType>;
};

export enum COLUMN_CONFIG_ERROR_TYPES {
  INVALID_LOOKUP_PATH = "Invalid_Lookup_Path",
  INVALID_LOOKUP_FILTERS = "Invalid_Lookup_Filters",
  INVALID_COLUMN_OPTIONS = "Invalid_Column_Options",
  INVALID_COLUMN_FILTERS = "Invalid_Column_Filters",
  INVALID_INLINE_CREATE = "Invalid_Inline_Create",
  INVALID_FORMULA = "Invalid_Formula",
  INVALID_COLUMN = "Invalid_Column",
  INVALID_JOIN_TABLE_FK_REF = "Join_Table_FK_Ref",
  INVALID_LOOKUP_COLUMNS = "Invalid_Lookup_Columns",
  INVALID_RECORD_TYPE_LOOKUP = "Invalid_Record_Type_Lookup",
  INVALID_RECORD_TYPE_COLUMN_OPTIONS = "Invalid_Record_Type_ColOptions"
}

export enum PAGE_ERROR_TYPES {
  INVALID_PAGE_CONFIG = "Invalid_Page_Config",
  INVALID_PAGE_TABLE = "Invalid_Page_Table",
  INVALID_PAGE_VIEW = "Invalid_Page_View"
}

export const PAGE_TYPES = {
  REPORTING: "reporting",
  DATABASE: "database",
  TEMPLATE: "template",
  STATIC: "static",
  MULTI_PAGES: "multi_pages"
};

export enum TILE_ARRAY_ITEM_TYPE {
  OBJECT = "object",
  STRING = "string",
  NUMBER = "number"
}

export type FeaturedImageDetails = {
  value?: string;
  type?: string;
  id?: string;
};

export enum CardSize {
  SMALL = "small",
  MEDIUM = "medium",
  LARGE = "large",
  EXTRA_LARGE = "extra large"
}

export enum DETAIL_TAB_VISIBILITY {
  SIDEBAR = "sidebar",
  DETAIL_TAB = "detail_tab",
  DETAIL_LIST_VIEW = "detail_list_view",
  ALL = "all"
}

export enum CONFIG_MODAL_TYPES {
  BASIC_PAGE = "basic_page",
  COLUMN_CONFIG = "column_config",
  PAGE_VIEW = "page_view",
  RECORD_TYPE = "record_type",
  FORM_VIEW = "form_view"
}

export type LookupAvailableTables = {
  "0"?: Array<{ tableName: string; tableType: string; fkeyColName?: string }>;
  "1"?: Array<{ tableName: string; tableType: string; fkeyColName?: string }>;
};

export type SIDEBAR_PARAMS_SET = {
  pageId: string;
  recordId: string;
  fileColId?: string;
};

export type FORM_PARAMS_SET = {
  title?: string;
  formId?: string;
  pageFormId?: string;
  pageId?: string; // Used to load page form
  sidebar?: SidebarContainer;
  tablePath?: string;
  action?: CrudActions;
  parentRecordId?: string;
  parentRecordPageId?: string;
  add?: {
    expanded?: boolean;
    addPageId: string;
    tabId?: string;
  };
  trigger?: FORM_TRIGGER_TYPES;
};

export type COLLAPSIBLE_VIEWER_PARAMS_SET = {
  removeViewerId?: string; // used to remove viewer item from collapsible Viewer
};

export type FILTERS_PARAMS_SET = {
  column: TableColumnType;
  filterOperator: string;
  filterValue: any;
  filterSlug: string;
  filterOrGroup?: FILTERS_PARAMS_SET[];
  filterGroupType?: string; // Used to identify Current User / Me filter
};

export type CUSTOM_SEARCH_FILTERS_PARAMS_SET = {
  filterOperator: string;
  filterValue: any;
  filterName: string;
  filterSlug: string;
};

export type CELL_SIDE_PARAMS_SET = {
  pageId: string;
  recordId: string;
  tabId?: string;
  fileColId?: string;
  type?: SidebarContainer;
  title?: string;
};

export type DEAL_QUICK_EXPAND_PARAMS_SET = {
  recordId?: string;
  activeTabSlug?: string;
  activeTabId?: string;
};

export type PAGE_QUERY_ADDITIONAL_PROP_TYPES = Array<
  | "views"
  | "tabs"
  | "columns"
  | "column_rules"
  | "columns_lite"
  | "views_lite"
  | "pageFilters"
  | "actions"
  | "sections"
  | "pageRoles"
>;

export type TableActionsContextCommonSearchProps = {
  resultIds?: string[];
  totalCount?: number;
  currentPage?: number;
  pageSize?: number;
};

export type TableActionContextSearchStateCustomsFiltersProps = TableActionsContextCommonSearchProps & {
  filterValues?: Record<string, any>;
};
export type TableActionsContextSearchState = TableActionsContextCommonSearchProps & {
  searchValue: string;
  /**
   * customSearchFilters: This is used for additional customFilters that can be applied along with Search
   * Currently its only used in ConversationsFilters UI for now, if we see more usecases in future
   * we can move this to a separate component that is reusable
   * */
  customSearchFilters?: TableActionContextSearchStateCustomsFiltersProps;
};

export type ActiveSearchContextState = {
  id: string;
  title: string;
  tableName: string;
  recordId?: string;
  isEntityType?: boolean; // When we are filtering by entityType on master index
};

export type InfoLoggerProps = { source: string; additionalInfo?: RecordItem };

export type OnRecordEditType =
  | SelectOptions
  | string
  | string[]
  | boolean
  | number
  | Array<File | BAFile>
  | SelectOption
  | AddressValue
  | undefined;

export type CRUDFormType = {
  title: string;
  indexName: string;
  linkedPage?: Partial<Page>;
  showInSidebar: boolean;
  hasCreateAction: boolean;
  enableGlobalSearch: boolean;
  resultColumn: Partial<TableColumnType>;
  additionalConfig?: SearchTableAdditionalConfig;
  columns?: Partial<TableColumnType>[];
  tableName?: string;
};

export type QueryKeyType = (string | boolean | string[] | undefined)[];

export type FilterValueType = string | SelectOption | boolean | string[];
